import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Loader2, Wrench } from "lucide-react";

interface MaintenanceGateProps {
  children: React.ReactNode;
}

export function MaintenanceGate({ children }: MaintenanceGateProps) {
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [maintenance, setMaintenance] = useState(false);
  const [message, setMessage] = useState("");
  const [isAdmin, setIsAdmin] = useState(false);
  const [isBypassUser, setIsBypassUser] = useState(false);

  // Specific users allowed to browse the site during maintenance (non-admin testers).
  const BYPASS_EMAILS = ["gapiii359@gmail.com"];

  // Routes that remain accessible even during maintenance so admins can sign in.
  const isBypassRoute =
    location.pathname.startsWith("/auth") ||
    location.pathname.startsWith("/admin");

  useEffect(() => {
    let active = true;

    const refreshAdmin = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user) {
        if (active) setIsAdmin(false);
        if (active) setIsBypassUser(false);
        return;
      }
      if (active) {
        setIsBypassUser(
          !!session.user.email &&
            BYPASS_EMAILS.includes(session.user.email.toLowerCase())
        );
      }
      const { data } = await supabase
        .from("user_roles")
        .select("role")
        .eq("user_id", session.user.id)
        .eq("role", "admin")
        .maybeSingle();
      if (active) setIsAdmin(!!data);
    };

    const loadSettings = async () => {
      const { data } = await supabase
        .from("site_settings")
        .select("key, value")
        .in("key", ["maintenance_mode", "maintenance_message"]);
      if (!active) return;
      const map = Object.fromEntries((data ?? []).map(r => [r.key, r.value]));
      setMaintenance(map.maintenance_mode === "true");
      setMessage(map.maintenance_message ?? "");
      setLoading(false);
    };

    refreshAdmin();
    loadSettings();

    const { data: { subscription } } = supabase.auth.onAuthStateChange(() => {
      refreshAdmin();
    });

    // Live updates if admin flips the toggle
    const channel = supabase
      .channel("maintenance-settings")
      .on(
        "postgres_changes",
        { event: "UPDATE", schema: "public", table: "site_settings" },
        (payload) => {
          const row = payload.new as { key: string; value: string };
          if (row.key === "maintenance_mode") setMaintenance(row.value === "true");
          if (row.key === "maintenance_message") setMessage(row.value);
        }
      )
      .subscribe();

    return () => {
      active = false;
      subscription.unsubscribe();
      supabase.removeChannel(channel);
    };
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
      </div>
    );
  }

  if (maintenance && !isAdmin && !isBypassUser && !isBypassRoute) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background px-6">
        <div className="max-w-md w-full text-center bg-card/60 backdrop-blur-sm border border-border/50 rounded-2xl p-10 shadow-xl">
          <div className="mx-auto w-16 h-16 rounded-full bg-primary/15 flex items-center justify-center mb-6">
            <Wrench className="h-8 w-8 text-primary" />
          </div>
          <h1 className="text-3xl font-bold mb-3">The Wait Is Almost Over!</h1>
          <p className="text-muted-foreground whitespace-pre-line">
            {message || "SOMETHING IS COMING VERY SOON!"}
          </p>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}

export default MaintenanceGate;